export const colors = {
  blue: "#2f88ff",
  green: "#62ce4d",
  amber: "#ff9f1c",
  red: "#ff493d",
  purple: "#b36bff",
  teal: "#2dd4bf",
  grid: "rgba(255,255,255,0.12)",
  text: "#dce7f3",
  muted: "#96a6b7",
};

export const patternWorkPhaseName = "Pattern Work";
export const phaseOrder = ["Startup", "Taxi", "Runup", "Ground Run", "Takeoff", "Climb", patternWorkPhaseName, "Cruise", "Descent", "Landing", "Shutdown"];
export const airbornePhaseNames = new Set(["Takeoff", "Climb", patternWorkPhaseName, "Cruise", "Descent", "Landing"]);
export const groundPhaseNames = new Set(["Startup", "Taxi", "Runup", "Ground Run"]);
export const patternWorkCandidatePhases = new Set(["Climb", "Cruise", "Descent"]);

export const chtHealthReviewThresholdF = 380;
export const chtCautionThresholdF = 400;
export const chtPeakReviewThresholdF = 420;
export const chtCoolingRateTriggerFpm = 50;
export const chtCoolingRateTriggerMinFpm = 25;
export const chtCoolingRateTriggerMaxFpm = 125;
export const chtCoolingRateMinWindowSeconds = 12;
export const chtCoolingRateMaxWindowSeconds = 75;

export const thermalAxisRanges = {
  egt: { label: "EGT (F)", min: 750, max: 1500, color: colors.purple },
  cht: { label: "CHT (F)", min: 175, max: 600, color: colors.amber },
};

export interface SeriesMetaEntry {
  label: string;
  color: string;
  min: number;
  max: number;
  auxGroup?: string;
  axisLabel?: string;
}

export const seriesMeta: Record<string, SeriesMetaEntry> = {
  egt: { label: "EGT (F)", color: colors.purple, min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  cht: { label: "CHT (F)", color: colors.amber, min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  egt1: { label: "EGT1", color: "#8c5cff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt2: { label: "EGT2", color: "#b36bff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt3: { label: "EGT3", color: "#d08bff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt4: { label: "EGT4", color: "#f0a6ff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt5: { label: "EGT5", color: "#c77dff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt6: { label: "EGT6", color: "#e0b0ff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt7: { label: "EGT7", color: "#9b5de5", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt8: { label: "EGT8", color: "#b15eff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt9: { label: "EGT9", color: "#d36cff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt10: { label: "EGT10", color: "#ff85e8", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt11: { label: "EGT11", color: "#c77dff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt12: { label: "EGT12", color: "#e0b0ff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt13: { label: "EGT13", color: "#8c5cff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt14: { label: "EGT14", color: "#b36bff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt15: { label: "EGT15", color: "#d08bff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  egt16: { label: "EGT16", color: "#f0a6ff", min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max },
  cht1: { label: "CHT1", color: "#ff9f1c", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht2: { label: "CHT2", color: "#ffb347", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht3: { label: "CHT3", color: "#ff493d", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht4: { label: "CHT4", color: "#ffd166", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht5: { label: "CHT5", color: "#ffbe63", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht6: { label: "CHT6", color: "#ffe08a", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht7: { label: "CHT7", color: "#f97316", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht8: { label: "CHT8", color: "#fb923c", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht9: { label: "CHT9", color: "#ef4444", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht10: { label: "CHT10", color: "#facc15", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht11: { label: "CHT11", color: "#fdba74", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht12: { label: "CHT12", color: "#fde68a", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht13: { label: "CHT13", color: "#ff9f1c", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht14: { label: "CHT14", color: "#ffb347", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht15: { label: "CHT15", color: "#ff493d", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  cht16: { label: "CHT16", color: "#ffd166", min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max },
  oilTemp: { label: "Oil Temp (F)", color: colors.blue, min: 60, max: 240, auxGroup: "temp", axisLabel: "Aux temp (F)" },
  coolantTemp: { label: "Coolant Temp (F)", color: "#14b8a6", min: 60, max: 240, auxGroup: "temp", axisLabel: "Aux temp (F)" },
  oilPressure: { label: "Oil Pressure (psi)", color: colors.green, min: 20, max: 100, auxGroup: "pressure", axisLabel: "Oil pressure (psi)" },
  fuelFlow: { label: "Fuel Flow (GPH)", color: colors.red, min: 0, max: 25, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelFlow1: { label: "Fuel Flow (GPH)", color: colors.red, min: 0, max: 25, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelFlow2: { label: "Fuel Flow (GPH)", color: "#ff7a70", min: 0, max: 25, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelFlowLeft: { label: "Fuel Flow (GPH)", color: colors.red, min: 0, max: 25, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelFlowRight: { label: "Fuel Flow (GPH)", color: "#ff7a70", min: 0, max: 25, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelUsed: { label: "Fuel Used (gal)", color: colors.teal, min: 0, max: 12, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelUsed1: { label: "Fuel Used (gal)", color: colors.teal, min: 0, max: 12, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelUsed2: { label: "Fuel Used (gal)", color: "#5eead4", min: 0, max: 12, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelUsedLeft: { label: "Fuel Used (gal)", color: colors.teal, min: 0, max: 12, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelUsedRight: { label: "Fuel Used (gal)", color: "#5eead4", min: 0, max: 12, auxGroup: "fuel", axisLabel: "Fuel (GPH / gal)" },
  fuelPressure: { label: "Fuel Pressure (psi)", color: "#fb7185", min: 0, max: 40, auxGroup: "pressure", axisLabel: "Fuel pressure (psi)" },
  fuelPressure1: { label: "Fuel Pressure (psi)", color: "#fb7185", min: 0, max: 40, auxGroup: "pressure", axisLabel: "Fuel pressure (psi)" },
  fuelPressure2: { label: "Fuel Pressure (psi)", color: "#fda4af", min: 0, max: 40, auxGroup: "pressure", axisLabel: "Fuel pressure (psi)" },
  percentPower: { label: "Engine Power (%)", color: "#f59e0b", min: 0, max: 100, auxGroup: "power", axisLabel: "Engine power (%)" },
  powerRopHp: { label: "Power ROP (hp)", color: "#fb923c", min: 0, max: 400, auxGroup: "horsepowerEstimate", axisLabel: "Theoretical power (hp)" },
  percentPowerRop: { label: "Power ROP (%)", color: "#f59e0b", min: 0, max: 100, auxGroup: "percentPowerEstimate", axisLabel: "Engine power estimate (%)" },
  powerLopHp: { label: "Power LOP (hp)", color: "#4ade80", min: 0, max: 400, auxGroup: "horsepowerEstimate", axisLabel: "Theoretical power (hp)" },
  percentPowerLop: { label: "Power LOP (%)", color: "#84cc16", min: 0, max: 100, auxGroup: "percentPowerEstimate", axisLabel: "Engine power estimate (%)" },
  rpm: { label: "Engine RPM", color: "#5eead4", min: 0, max: 3000, auxGroup: "power", axisLabel: "Engine RPM" },
  rpm1: { label: "Engine RPM", color: "#5eead4", min: 0, max: 3000, auxGroup: "power", axisLabel: "Engine RPM" },
  rpm2: { label: "Engine RPM", color: "#99f6e4", min: 0, max: 3000, auxGroup: "power", axisLabel: "Engine RPM" },
  rpmLeft: { label: "Engine RPM", color: "#5eead4", min: 0, max: 3000, auxGroup: "power", axisLabel: "Engine RPM" },
  rpmRight: { label: "Engine RPM", color: "#99f6e4", min: 0, max: 3000, auxGroup: "power", axisLabel: "Engine RPM" },
  map: { label: "MAP (inHg)", color: "#f472b6", min: 10, max: 32, auxGroup: "map", axisLabel: "Manifold pressure (inHg)" },
  map1: { label: "MAP (inHg)", color: "#f472b6", min: 10, max: 32, auxGroup: "map", axisLabel: "Manifold pressure (inHg)" },
  map2: { label: "MAP (inHg)", color: "#f9a8d4", min: 10, max: 32, auxGroup: "map", axisLabel: "Manifold pressure (inHg)" },
  mapLeft: { label: "MAP (inHg)", color: "#f472b6", min: 10, max: 32, auxGroup: "map", axisLabel: "Manifold pressure (inHg)" },
  mapRight: { label: "MAP (inHg)", color: "#f9a8d4", min: 10, max: 32, auxGroup: "map", axisLabel: "Manifold pressure (inHg)" },
  tit: { label: "TIT (F)", color: "#ff6b35", min: 800, max: 1700, auxGroup: "turboTemp", axisLabel: "Turbo temp (F)" },
  tit1: { label: "TIT (F)", color: "#ff6b35", min: 800, max: 1700, auxGroup: "turboTemp", axisLabel: "Turbo temp (F)" },
  tit2: { label: "TIT (F)", color: "#ff8f5a", min: 800, max: 1700, auxGroup: "turboTemp", axisLabel: "Turbo temp (F)" },
  upperDeckPressure: { label: "Upper Deck (psi)", color: "#f59e0b", min: 0, max: 40, auxGroup: "boost", axisLabel: "Boost / deck pressure (psi)" },
  inductionTemp: { label: "Induction Temp (F)", color: "#38bdf8", min: 30, max: 250, auxGroup: "turboTemp", axisLabel: "Turbo temp (F)" },
  compressorDischargeTemp: { label: "Compressor Temp (F)", color: "#06b6d4", min: 30, max: 300, auxGroup: "turboTemp", axisLabel: "Turbo temp (F)" },
  buss: { label: "Bus Volts", color: colors.blue, min: 10, max: 15, auxGroup: "electrical", axisLabel: "Bus volts" },
  amps: { label: "Amps", color: "#22c55e", min: -20, max: 60, auxGroup: "current", axisLabel: "Current (A)" },
  amps1: { label: "Amps", color: "#22c55e", min: -20, max: 80, auxGroup: "current", axisLabel: "Current (A)" },
  amps2: { label: "Amps", color: "#86efac", min: -20, max: 80, auxGroup: "current", axisLabel: "Current (A)" },
  ampsLeft: { label: "Amps", color: "#22c55e", min: -20, max: 80, auxGroup: "current", axisLabel: "Current (A)" },
  ampsRight: { label: "Amps", color: "#86efac", min: -20, max: 80, auxGroup: "current", axisLabel: "Current (A)" },
  oat: { label: "OAT (F)", color: "#9fb3c8", min: 30, max: 110, auxGroup: "temp", axisLabel: "Aux temp (F)" },
  carbTemp: { label: "Carb Temp (F)", color: "#f7cf63", min: 30, max: 140, auxGroup: "temp", axisLabel: "Aux temp (F)" },
  oilTemp1: { label: "Oil Temp (F)", color: colors.blue, min: 60, max: 240, auxGroup: "temp", axisLabel: "Aux temp (F)" },
  oilTemp2: { label: "Oil Temp (F)", color: "#60a5fa", min: 60, max: 240, auxGroup: "temp", axisLabel: "Aux temp (F)" },
  oilTempLeft: { label: "Oil Temp (F)", color: colors.blue, min: 60, max: 240, auxGroup: "temp", axisLabel: "Aux temp (F)" },
  oilTempRight: { label: "Oil Temp (F)", color: "#60a5fa", min: 60, max: 240, auxGroup: "temp", axisLabel: "Aux temp (F)" },
  oilPressure1: { label: "Oil Pressure (psi)", color: colors.green, min: 20, max: 100, auxGroup: "pressure", axisLabel: "Oil pressure (psi)" },
  oilPressure2: { label: "Oil Pressure (psi)", color: "#86efac", min: 20, max: 100, auxGroup: "pressure", axisLabel: "Oil pressure (psi)" },
  oilPressureLeft: { label: "Oil Pressure (psi)", color: colors.green, min: 20, max: 100, auxGroup: "pressure", axisLabel: "Oil pressure (psi)" },
  oilPressureRight: { label: "Oil Pressure (psi)", color: "#86efac", min: 20, max: 100, auxGroup: "pressure", axisLabel: "Oil pressure (psi)" },
  ias: { label: "IAS (kt)", color: "#61dafb", min: 0, max: 180, auxGroup: "air", axisLabel: "Air data" },
  tas: { label: "TAS (kt)", color: "#7dd3fc", min: 0, max: 200, auxGroup: "air", axisLabel: "Air data" },
  groundSpeed: { label: "Ground Speed (kt)", color: "#38bdf8", min: 0, max: 200, auxGroup: "air", axisLabel: "Air data" },
  altitude: { label: "Altitude (ft)", color: "#a7f3d0", min: 0, max: 14000, auxGroup: "altitude", axisLabel: "Altitude (ft)" },
  verticalSpeed: { label: "Vertical Speed (fpm)", color: "#facc15", min: -1500, max: 1500, auxGroup: "vertical", axisLabel: "Vertical speed (fpm)" },
  pitch: { label: "Pitch (deg)", color: "#f7cf63", min: -30, max: 30, auxGroup: "attitude", axisLabel: "Attitude (deg)" },
  roll: { label: "Roll (deg)", color: "#61dafb", min: -60, max: 60, auxGroup: "attitude", axisLabel: "Attitude (deg)" },
};

interface SeriesEngineDescriptor {
  label?: string;
  shortLabel?: string;
  channels?: Record<string, unknown>;
}

function seriesEnginePrefix(descriptor: SeriesEngineDescriptor | null | undefined): string {
  if (!descriptor) return "";
  return String(descriptor.label ?? descriptor.shortLabel ?? "").replace(/\s+Engine$/i, "").trim();
}

function buildSeriesEngineKeyMap(descriptors: SeriesEngineDescriptor[] = []): Map<string, SeriesEngineDescriptor> {
  const map = new Map<string, SeriesEngineDescriptor>();
  if (!Array.isArray(descriptors) || descriptors.length < 2) return map;
  for (const descriptor of descriptors) {
    const channels = descriptor?.channels ?? {};
    for (const channelKey of Object.values(channels)) {
      if (typeof channelKey === "string" && channelKey) map.set(channelKey, descriptor);
    }
  }
  return map;
}

export function buildSeriesMeta(descriptors: SeriesEngineDescriptor[] = []): Record<string, SeriesMetaEntry> {
  const keyToEngine = buildSeriesEngineKeyMap(descriptors);
  if (!keyToEngine.size) return seriesMeta;
  const next: Record<string, SeriesMetaEntry> = {};
  for (const [key, value] of Object.entries(seriesMeta)) {
    const prefix = seriesEnginePrefix(keyToEngine.get(key));
    next[key] = prefix ? { ...value, label: `${prefix} ${value.label}` } : value;
  }
  return next;
}

export const sampleDataPath = "data/N23508/05280825.CSV";
export const sampleAircraftTailNumber = "N000XX";
export const legacySampleAircraftTailNumber = "N23508";
export const secondarySampleAircraftTailNumber = "N2000AX";
export const secondaryLegacySampleAircraftTailNumber = "N9720X";
export const tertiarySampleAircraftTailNumber = "N7478XY";
export const tertiaryLegacySampleAircraftTailNumber = "N814PX";
// Committed synthetic sample flight (Garmin-style, generated by
// scripts/generate_synthetic_condition_logs.mjs) shown when no real flight
// logs exist — keep the .gitignore exception for this path in sync.
export const fallbackDataPath = "data/garmin_sample_flight.csv";
export const flightManifestPath = "data/flight_manifest.json";
export const aircraftProfilesPath = "data/aircraft_profiles.json";
export const appConfigEndpoint = "/api/app-config";
export const contactRequestEndpoint = "/api/contact";
export const databaseAircraftEndpoint = "/api/aircraft";
export const databaseUploadEndpoint = "/api/uploads";
export const databaseUploadDeleteEndpoint = (uploadId: string) => `${databaseUploadEndpoint}/${encodeURIComponent(uploadId)}`;
export const databaseUploadReprocessEndpoint = (uploadId: string) => `${databaseUploadEndpoint}/${encodeURIComponent(uploadId)}/reprocess`;
export const databaseUploadProcessShortFlightsEndpoint = (uploadId: string) => `${databaseUploadEndpoint}/${encodeURIComponent(uploadId)}/process-short-flights`;
export const databaseFlightDeleteEndpoint = (flightId: string) => `/api/flights/${encodeURIComponent(flightId)}`;
export const databaseFlightArchiveEndpoint = (flightId: string) => `/api/flights/${encodeURIComponent(flightId)}/archive`;
export const databaseFlightUnarchiveEndpoint = (flightId: string) => `/api/flights/${encodeURIComponent(flightId)}/unarchive`;
export const databaseAircraftArchiveEndpoint = (aircraftId: string) => `${databaseAircraftEndpoint}/${encodeURIComponent(aircraftId)}/archive`;
export const databaseAircraftUnarchiveEndpoint = (aircraftId: string) => `${databaseAircraftEndpoint}/${encodeURIComponent(aircraftId)}/unarchive`;
export const accountArchiveEndpoint = "/api/account/archive";
export const databaseIngestJobEndpoint = (jobId: string) => `/api/jobs/${encodeURIComponent(jobId)}`;
export const databaseAircraftUploadsEndpoint = (aircraftId: string) => `${databaseAircraftEndpoint}/${encodeURIComponent(aircraftId)}/uploads`;
export const databaseTrendSnapshotEndpoint = (aircraftId: string) => `${databaseAircraftEndpoint}/${encodeURIComponent(aircraftId)}/trend-snapshot`;
export const databaseTrendAiSummaryEndpoint = (aircraftId: string) => `${databaseAircraftEndpoint}/${encodeURIComponent(aircraftId)}/trend-ai-summary`;
export const databaseAircraftFlightFlagSummariesEndpoint = (aircraftId: string) => `${databaseAircraftEndpoint}/${encodeURIComponent(aircraftId)}/flight-flag-summaries`;
export const databaseFlightAiSummaryEndpoint = (flightId: string) => `/api/flights/${encodeURIComponent(flightId)}/ai-summary`;
export const reviewNoteMaxLength = 500;
export const databaseFlightReviewNotesEndpoint = (flightId: string, cueKey = "") => {
  const base = `/api/flights/${encodeURIComponent(flightId)}/review-notes`;
  return cueKey ? `${base}/${encodeURIComponent(cueKey)}` : base;
};
export const graphPartQuestionMaxLength = 300;
export const databaseFlightAskGraphPartEndpoint = (flightId: string) => `/api/flights/${encodeURIComponent(flightId)}/ask-graph-part`;
export const databaseFlightSpecialTestEndpoint = (flightId: string, testKind = "", resultId = "") => {
  const base = `/api/flights/${encodeURIComponent(flightId)}/special-tests`;
  const kindPath = testKind ? `${base}/${encodeURIComponent(testKind)}` : base;
  return resultId ? `${kindPath}/${encodeURIComponent(resultId)}` : kindPath;
};
export const valvePatternSummaryPath = "data/valve_pattern_summary.json";
export const hiddenFlightsStorageKey = "aeroInsightHiddenFlightIds";
export const trendExcludedFlightsStorageKey = "aeroInsightTrendExcludedFlightIds";
export const trendIncludedFlightsStorageKey = "aeroInsightTrendIncludedFlightIds";
export const sensitivityStorageKey = "aeroInsightDetectionSensitivity";
export const aircraftReviewSettingsStorageKey = "aeroInsightAircraftReviewSettings";
export const selectedAircraftWorkspaceStorageKey = "aeroInsightSelectedAircraftWorkspace";
export const selectedFlightStorageKey = "aeroInsightSelectedFlightId";
export const seriesPaneOverridesStorageKey = "aeroInsightSeriesPaneOverrides";
export const sensitivityModeOrder = ["conservative", "balanced", "sensitive"];

// --- Account > Settings preferences ----------------------------------------
// Per-device display preferences, stored client-side like the rest of this
// file's keys: they change what YOU see, not what the account owns, so they
// deliberately do not round-trip through the server.
export const themePreferenceStorageKey = "aeroInsightThemePreference";
export const aircraftOrderStorageKey = "aeroInsightAircraftOrder";
export const flightOrderStorageKey = "aeroInsightFlightOrder";

// "system" follows the OS via prefers-color-scheme. "dark" is what the site
// has always looked like and stays the fallback for anything unrecognised.
export const themePreferenceOrder = ["system", "dark", "light"];
export const defaultThemePreference = "dark";

// All computed orders. Drag-to-reorder (aircraft.display_order) is no longer
// offered here, so "created" — the order aircraft were added — is the default
// because it is the closest stable stand-in for a hand-built fleet list.
export const aircraftOrderOptions = ["created", "tail", "recent", "flights"];
export const defaultAircraftOrder = "created";

export const flightOrderOptions = ["newest", "oldest"];
export const defaultFlightOrder = "newest";

// Which generated default-art view rests in aircraft image slots when there
// is no uploaded photo; hover shows the other view.
export const aircraftArtViewStorageKey = "aeroInsightAircraftArtView";
export const aircraftArtViewOptions = ["top_down", "side_views"];
export const defaultAircraftArtView = "top_down";
export const sensitivityModes = {
  conservative: {
    label: "Conservative",
    note: "Requires stronger evidence before a cue lowers the score.",
    penaltyScale: 0.16,
  },
  balanced: {
    label: "Balanced",
    note: "Recommended. Scores notable cues without chasing every small variation.",
    penaltyScale: 0.45,
  },
  sensitive: {
    label: "Sensitive",
    note: "Scores smaller deviations and early cues more aggressively.",
    penaltyScale: 1,
  },
};

export const trendMetricMeta = {
  overallScore: { label: "Overall score", suffix: "", digits: 0, min: 0, max: 100, color: colors.blue },
  sensorScore: { label: "Sensor/data quality", suffix: "", digits: 0, min: 0, max: 100, color: colors.teal },
  chtScore: { label: "CHT heat", suffix: "", digits: 0, min: 0, max: 100, color: colors.amber },
  egtScore: { label: "EGT balance", suffix: "", digits: 0, min: 0, max: 100, color: colors.purple },
  valveScore: { label: "EGT rhythm", suffix: "", digits: 0, min: 0, max: 100, color: colors.green },
  combustionScore: { label: "CHT/EGT pairing", suffix: "", digits: 0, min: 0, max: 100, color: colors.red },
  divergenceScore: { label: "Transient divergence", suffix: "", digits: 0, min: 0, max: 100, color: colors.teal },
  oilSystemScore: { label: "Oil system", suffix: "", digits: 0, min: 0, max: 100, color: colors.amber },
  descentEgtDropoutScore: { label: "Descent EGT dropout", suffix: "", digits: 0, min: 0, max: 100, color: colors.purple },
  inductionLeakScore: { label: "Induction leak", suffix: "", digits: 0, min: 0, max: 100, color: colors.amber },
  magScore: { label: "Mag check", suffix: "", digits: 0, min: 0, max: 100, color: colors.amber },
  carbIceScore: { label: "Carb ice", suffix: "", digits: 0, min: 0, max: 100, color: "#f7cf63" },
  coolingScore: { label: "Cooling", suffix: "", digits: 0, min: 0, max: 100, color: colors.teal },
  coolingRateScore: { label: "Cooling rate", suffix: "", digits: 0, min: 0, max: 100, color: colors.amber },
  chargingScore: { label: "Start charge", suffix: "", digits: 0, min: 0, max: 100, color: colors.blue },
  electricalScore: { label: "Electrical", suffix: "", digits: 0, min: 0, max: 100, color: colors.amber },
  turboScore: { label: "Turbo/boost review", suffix: "", digits: 0, min: 0, max: 100, color: colors.amber },
  gamiSweepScore: { label: "GAMI sweep", suffix: "", digits: 0, min: 0, max: 100, color: colors.teal },
  gamiSweepSpreadGph: { label: "GAMI spread", suffix: " GPH", digits: 2, color: colors.teal },
  peakCht: { label: "Peak CHT", suffix: " F", digits: 0, color: colors.red },
  maxTit: { label: "Peak TIT", suffix: " F", digits: 0, color: "#ff6b35" },
  titP95: { label: "TIT p95", suffix: " F", digits: 0, color: "#ff8f5a" },
  maxMap: { label: "Peak MAP", suffix: " inHg", digits: 1, color: "#f472b6" },
  coolingRateFpm: { label: "Max CHT cooling", suffix: " F/min", digits: 0, color: colors.amber, invertYAxis: true },
  egtSpreadP90: { label: "EGT cylinder gap", suffix: " F", digits: 0, color: colors.purple },
  busRange: { label: "Bus voltage range", suffix: " V", digits: 1, color: colors.blue },
};

export const powerEstimateConfig = {
  fuelDensityLbPerGal: 6.0,
  bsfcLbPerHpHour: 0.50,
  minFuelFlowGph: 1.5,
  minTorqueRpm: 900,
};
